// Cloud Ontology Editor.
//
// Online schema (T-box) editing for clients who can't run a desktop editor
// (the 0608 driver). Two-panel layout mirroring the Vocabulary tab: left is
// the class tree (rdfs:subClassOf hierarchy) + a properties list; right is
// the detail panel for the selected class or property, with inline edit and
// delete. Create flows are modals.
//
// Design rules (CLOUD_ONTOLOGY_EDITOR_DESIGN.md):
//   - Vocabulary is OWL: owl:Class / owl:ObjectProperty / owl:DatatypeProperty.
//     owl:Thing is the implicit top — never shown as an asserted parent.
//   - New term IRIs are readable (server derives <base>:Slug from the label).
//   - Reads/writes go through the substrate, so the editor shows exactly what
//     is declared and an edit round-trips immediately.
//   - Writes need ImportData (Admin + Power User); reads are open to any
//     authenticated user (read-only for the User role).

import { useEffect, useMemo, useRef, useState } from 'react';
import { DisjointRuleForm } from '@/components/DisjointRuleForm';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { invalidateAfterEdit } from '@/lib/invalidate';
import { useShownRows } from '@/lib/useShownRows';
import { ShowMoreButton } from '@/components/ShowMoreButton';
import { listNamespaces, type NamespaceList } from '@/lib/api';
import { useAuth } from 'react-oidc-context';
import { ALL_GRAPHS, graphDisplayName } from '@/components/GraphSelector';
import { GraphMultiPicker } from '@/components/GraphMultiPicker';
import { useGraphScope } from '@/lib/useGraphScope';
import { useGraphs } from '@/lib/useGraphs';
import { DocExportButtons } from '@/components/DocExportButtons';
import { ReadinessPanel } from '@/pages/ReadinessPanel';
import { canImportData, isQros, useMe } from '@/lib/useMe';
import { EditModeButton } from '@/lib/editMode';
import { OntologyAbout } from '@/components/OntologyAbout';
import { useDescribedOntologies } from '@/lib/ontologyAbout';
import { SubTabs } from '@/components/SubTabs';
import { mergeEntity, useEntity } from '@/lib/useEntity';
import {
  AltLabelsEditor,
  CopyUriButton,
  EntityView,
  QROS_ALT_LABEL,
  SKOS_ALT_LABEL,
} from '@/pages/EntityInfoPage';
import { cn } from '@/lib/utils';
import { baseClassForSlot, baseClassLocalName, colorByIndex } from '@/lib/baseClassColors';
import { useVdbClassColors, type VdbClassColor } from '@/lib/useVdb';
import {
  TagEditor,
  EMPTY_TAG_DRAFT,
  serializeTags,
  unpickedContext,
  type TagDraft,
} from '@/components/TagEditor';
import { AnnotationListEditor } from '@/components/AnnotationListEditor';
import {
  buildClassForest,
  countForest,
  createClass,
  deriveSchemaNamespace,
  incomingReferences,
  renameTerm,
  slugifyLocalName,
  splitUri,
  termReferences,
  createProperty,
  createTag,
  deleteClass,
  deleteProperty,
  labelOf,
  localName,
  patchClass,
  patchProperty,
  propertiesForClass,
  useOntologySchema,
  useOntologySchemas,
  TERM_ANNOTATION_PREDICATES,
  type AnnotationValue,
  type ClassTreeNode,
  type CreateClassRequest,
  type CreatePropertyRequest,
  type OntologySchema,
  type PatchClassRequest,
  type PatchPropertyRequest,
  type PropertyKind,
  type SchemaClass,
  type SchemaProperty,
  type TermReferences,
} from '@/lib/useOntology';
import { messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

const ONTOLOGY_GRAPH = 'urn:tres:ontology';

const GRAPHS_STORAGE_KEY = 'graphMultiPicker.ontology';

/** Common XSD datatypes offered as the range of a datatype property
 *  (select-only — taxonomists don't hand-type IRIs). */
const XSD_DATATYPES: { uri: string; label: string }[] = [
  { uri: 'http://www.w3.org/2001/XMLSchema#string', label: 'string' },
  { uri: 'http://www.w3.org/2001/XMLSchema#integer', label: 'integer' },
  { uri: 'http://www.w3.org/2001/XMLSchema#decimal', label: 'decimal' },
  { uri: 'http://www.w3.org/2001/XMLSchema#boolean', label: 'boolean' },
  { uri: 'http://www.w3.org/2001/XMLSchema#date', label: 'date' },
  { uri: 'http://www.w3.org/2001/XMLSchema#dateTime', label: 'dateTime' },
  { uri: 'http://www.w3.org/2001/XMLSchema#anyURI', label: 'anyURI' },
  { uri: 'http://www.w3.org/2001/XMLSchema#double', label: 'double' },
];

type Selection = { kind: 'class' | 'property'; uri: string } | null;

export function OntologyEditorPage() {
  const auth = useAuth();
  const me = useMe();
  const queryClient = useQueryClient();
  // Read scope: the SET of graphs whose declarations the tree shows.
  const [graphs, setGraphs] = useGraphScope(GRAPHS_STORAGE_KEY, [ONTOLOGY_GRAPH]);
  // Write target: a single graph, always. A patch/delete addressed at the union
  // (or at a graph the term is not declared in) reports success having changed
  // nothing, so the target is derived, shown, and never implicit.
  const [writeGraph, setWriteGraph] = useState<string | null>(null);
  const [selected, setSelected] = useState<Selection>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [classModalOpen, setClassModalOpen] = useState(false);
  const [propertyModalOpen, setPropertyModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  // Deep-link from the dashboard readiness card (`/ontology?readiness=1`) opens
  // the panel on arrival; read once at mount.
  const [searchParams] = useSearchParams();
  const [showReadiness, setShowReadiness] = useState(searchParams.get('readiness') === '1');

  const token = auth.user?.id_token;
  const [editMode, setEditMode] = useState(false);
  const canEdit = canImportData(me.data) && editMode;
  // QROS-native definition tags — the RDF version surfaces none.
  const qros = isQros(me.data);
  // The heading names an ontology only when one is described; a file's bare header
  // (a name and a version) is not one (6.9, 6.10).
  const about = useDescribedOntologies(graphs);
  const described = about.data?.length === 1 ? about.data[0] : undefined;
  const schemaQuery = useOntologySchemas(graphs, token);
  const classColors = useVdbClassColors().data;
  const schema = schemaQuery.data;
  const allGraphs = useGraphs();
  // Merge targets are offered UNION-wide: class/property identity is global,
  // and the natural target (e.g. a vocabulary-layer top class) often lives in
  // a different managed graph than the one being edited. Only the editing
  // controls read it (merge, rename, the new-term checks), so it is fetched
  // once edit mode is on, not for every reader of the page.
  const unionSchemaQuery = useOntologySchema(canEdit ? ALL_GRAPHS : null, token);
  const mergeClassOptions = useMemo(() => {
    const cs = unionSchemaQuery.data?.classes ?? schema?.classes ?? [];
    return cs
      .map((c) => ({ uri: c.uri, label: labelOf(c) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [unionSchemaQuery.data, schema]);
  const mergePropertyOptions = useMemo(() => {
    const ps = unionSchemaQuery.data?.properties ?? schema?.properties ?? [];
    return ps
      .map((p) => ({ uri: p.uri, label: labelOf(p) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [unionSchemaQuery.data, schema]);

  // The graphs a write may target: the concrete graphs in scope, or every named
  // graph when the scope is the union (a write can never address the union).
  const writeCandidates = useMemo(() => {
    if (!graphs.includes(ALL_GRAPHS)) return graphs;
    return (allGraphs.data ?? []).map((g) => g.bare);
  }, [graphs, allGraphs.data]);

  // Keep the write target inside the candidate set as the scope changes.
  useEffect(() => {
    if (writeGraph && writeCandidates.includes(writeGraph)) return;
    setWriteGraph(writeCandidates[0] ?? null);
  }, [writeGraph, writeCandidates]);

  const labelMap = useMemo(() => buildLabelMap(schema), [schema]);
  const needle = searchQuery.trim().toLowerCase();

  const forest = useMemo(
    () => (schema ? buildClassForest(schema.classes) : []),
    [schema],
  );
  const properties = useMemo(
    () => [...(schema?.properties ?? [])].sort((a, b) => labelOf(a).localeCompare(labelOf(b))),
    [schema],
  );

  const filteredForest = useMemo(() => filterForest(forest, needle), [forest, needle]);
  const filteredProperties = useMemo(
    () => (needle ? properties.filter((p) => matchesTerm(p, needle)) : properties),
    [properties, needle],
  );

  // The tree opens on its top classes with one level below each, never the
  // whole tree: an ontology of a few thousand classes would otherwise draw
  // every one of them before the page could answer.
  const [didAutoExpand, setDidAutoExpand] = useState(false);
  useEffect(() => {
    if (!schema || didAutoExpand) return;
    setExpanded(new Set(forest.map((root) => root.cls.uri)));
    setDidAutoExpand(true);
  }, [schema, forest, didAutoExpand]);

  // While searching, reveal every still-visible node. Keyed on the SEARCH TERM,
  // not on the filtered forest: `filterForest` builds fresh nodes on every call
  // with a needle, so depending on its result re-ran this on every render, and
  // each run set state and caused the next one (list 27.29).
  useEffect(() => {
    if (!needle) return;
    setExpanded(new Set(collectForestUris(filterForest(forest, needle))));
  }, [forest, needle]);

  function toggleNode(uri: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uri)) next.delete(uri);
      else next.add(uri);
      return next;
    });
  }

  const shownRoots = useShownRows(filteredForest);
  const shownProperties = useShownRows(filteredProperties);

  const isFiltering = needle !== '';
  const classCount = schema?.classes.length ?? 0;
  const visibleClassCount = countForest(filteredForest);

  // A link in a term's statements selects the class or property it names when
  // the editor shows it; any other term opens on the Entities page.
  const navigate = useNavigate();
  const openTerm = (uri: string) => {
    if (schema?.classes.some((c) => c.uri === uri)) {
      setSelected({ kind: 'class', uri });
    } else if (schema?.properties.some((p) => p.uri === uri)) {
      setSelected({ kind: 'property', uri });
    } else {
      navigate(`/entity?${new URLSearchParams({ uri }).toString()}`);
    }
  };

  // A link from another page (the Viz details pane) opens a term here: `term`
  // names it and `graph` the graph it is declared in, which joins the scope so
  // the tree can show it. The term is selected and its branch opened once the
  // schema of that scope has arrived; a term the editor does not show opens on
  // the Entities page, as a link inside a term's statements does.
  const [linkedTerm, setLinkedTerm] = useState(() => searchParams.get('term'));
  const [linkedGraph] = useState(() => searchParams.get('graph'));
  const linkedGraphInScope =
    !linkedGraph || graphs.includes(linkedGraph) || graphs.includes(ALL_GRAPHS);
  const scrollToSelected = useRef(false);
  useEffect(() => {
    if (!linkedTerm || !linkedGraph || linkedGraphInScope) return;
    setGraphs([...graphs, linkedGraph]);
  }, [linkedTerm, linkedGraph, linkedGraphInScope, graphs, setGraphs]);
  useEffect(() => {
    if (!linkedTerm || !linkedGraphInScope || !schema || !didAutoExpand) return;
    setLinkedTerm(null);
    if (schema.classes.some((c) => c.uri === linkedTerm)) {
      const ancestors = ancestorsInSchema(linkedTerm, schema.classes);
      setExpanded((prev) => new Set([...prev, ...ancestors]));
      setSelected({ kind: 'class', uri: linkedTerm });
      scrollToSelected.current = true;
    } else if (schema.properties.some((p) => p.uri === linkedTerm)) {
      setSelected({ kind: 'property', uri: linkedTerm });
      scrollToSelected.current = true;
    } else {
      navigate(`/entity?${new URLSearchParams({ uri: linkedTerm }).toString()}`);
    }
  }, [linkedTerm, linkedGraphInScope, schema, didAutoExpand, navigate]);
  useEffect(() => {
    if (!scrollToSelected.current || !selected) return;
    const row = document.querySelector(`[data-term="${CSS.escape(selected.uri)}"]`);
    if (!row) return;
    scrollToSelected.current = false;
    row.scrollIntoView({ block: 'center' });
  }, [selected, expanded]);

  const selectedClass =
    selected?.kind === 'class' ? schema?.classes.find((c) => c.uri === selected.uri) : undefined;
  const selectedProperty =
    selected?.kind === 'property'
      ? schema?.properties.find((p) => p.uri === selected.uri)
      : undefined;

  // The read-only entity view (QROS: statement tags/constraints) AND the
  // alt-labels editor (both backends) hang off the selected term's entity
  // payload. Read at the WRITE graph, not the union: reading the union while
  // writing one graph let a retract report success having removed a statement
  // that lives elsewhere. `home_graphs` comes back regardless of the graph
  // asked for, so the panel can still say where else the term is declared.
  const detailEntity = useEntity(selected ? selected.uri : null, writeGraph);
  const termHomeGraphs = useMemo(
    () => detailEntity.data?.home_graphs ?? [],
    [detailEntity.data],
  );

  // Land the write target on the graph the selected term actually lives in —
  // the same auto-shift the Entity page uses. Only when it is unambiguous; a
  // term declared in several graphs keeps the current target and offers the
  // chips below.
  useEffect(() => {
    const only = termHomeGraphs.length === 1 ? termHomeGraphs[0] : undefined;
    if (!only || only === writeGraph) return;
    // Never shift outside the scope — the effect above would pull it straight
    // back, and the two would ping-pong.
    if (!writeCandidates.includes(only)) return;
    setWriteGraph(only);
  }, [termHomeGraphs, writeGraph, writeCandidates]);

  // Pick a write target explicitly. A graph outside the read scope becomes the
  // scope as well, so the tree on screen and the graph being written can never
  // disagree.
  const chooseWriteGraph = (g: string) => {
    if (!writeCandidates.includes(g)) setGraphs([g]);
    setWriteGraph(g);
  };

  return (
    <div className="container mx-auto py-6">
      <SubTabs
        tabs={[
          { to: '/ontology', label: 'Ontology' },
          ...(qros ? [{ to: '/rules', label: 'Rules', page: 'rules' }] : []),
        ]}
      />
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-tres-text-primary">
            Ontology Editor
            {described && (
              <span className="ml-2 align-middle text-base font-normal text-tres-text-secondary">
                — {described.values.label?.[0]?.value ?? localName(described.iri)}
                {described.values.versionInfo?.[0] && (
                  <span className="ml-1.5 text-xs">({described.values.versionInfo[0].value})</span>
                )}
              </span>
            )}
          </h1>
          <p className="text-sm text-tres-text-secondary">
            Author classes and properties in the selected schema.
            {!canImportData(me.data) && ' Read-only — your role cannot edit the schema.'}
          </p>
        </div>
        <div className="flex items-end gap-2">
          <EditModeButton
            on={editMode}
            onToggle={setEditMode}
            visible={canImportData(me.data)}
          />
          {/* Documentation is generated from one graph server-side; export what
              the user is authoring in, not an arbitrary member of the scope. */}
          <DocExportButtons kind="ontology" graph={writeGraph} />
          <GraphMultiPicker
            selected={graphs}
            onChange={(next) => {
              setGraphs(next);
              setSelected(null);
              setExpanded(new Set());
              setDidAutoExpand(false);
            }}
          />
          {canImportData(me.data) && graphs.length > 0 && (
            <label className="flex flex-col gap-0.5 text-xs text-tres-text-secondary">
              Edits write to
              <select
                value={writeGraph ?? ''}
                onChange={(e) => setWriteGraph(e.target.value)}
                className="rounded-md border border-border bg-card px-2 py-1 text-xs text-tres-text-primary"
              >
                {writeCandidates.map((g) => (
                  <option key={g} value={g} title={g}>
                    {graphDisplayName(g)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            onClick={() => setShowReadiness(true)}
            className={buttonVariants({ variant: 'outline', size: 'md' })}
          >
            Readiness
          </button>
        </div>
      </div>
      <ReadinessPanel
        open={showReadiness}
        onClose={() => setShowReadiness(false)}
        canEdit={canImportData(me.data)}
      />
      <OntologyAbout graphs={graphs} writeGraph={writeGraph} canEdit={canEdit} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(300px,1fr)_2fr]">
        {/* Left: classes + properties */}
        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border px-3 py-2">
            {canEdit && (
              <div className="mb-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setClassModalOpen(true)}
                  disabled={!writeGraph}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                  title="Create a new class"
                >
                  + Class
                </button>
                <button
                  type="button"
                  onClick={() => setPropertyModalOpen(true)}
                  disabled={!writeGraph}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                  title="Create a new property"
                >
                  + Property
                </button>
                {qros && (
                  <button
                    type="button"
                    onClick={() => setTagModalOpen(true)}
                    disabled={!writeGraph}
                    className={buttonVariants({ variant: 'outline', size: 'sm' })}
                    title="Declare a reusable statement tag (@name) — it then appears in every tag editor"
                  >
                    + Tag
                  </button>
                )}
              </div>
            )}
            <div className="mb-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setExpanded(new Set((schema?.classes ?? []).map((c) => c.uri)))}
                disabled={!schema}
                className={buttonVariants({ variant: 'secondary', size: 'sm' })}
              >
                Expand all
              </button>
              <button
                type="button"
                onClick={() => setExpanded(new Set())}
                disabled={!schema}
                className={buttonVariants({ variant: 'secondary', size: 'sm' })}
              >
                Collapse all
              </button>
            </div>
            <div className="relative">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search classes and properties…"
                className="w-full rounded-md border border-border px-3 py-1.5 pr-8 text-sm text-tres-text-primary placeholder:text-tres-text-secondary focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
              />
              {isFiltering && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'absolute right-2 top-1/2')}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="mt-1.5 flex items-center justify-between text-xs text-tres-text-secondary">
              <span>
                {isFiltering
                  ? `${visibleClassCount}/${classCount} classes · ${filteredProperties.length}/${properties.length} properties`
                  : `${classCount} class${classCount === 1 ? '' : 'es'} · ${properties.length} propert${properties.length === 1 ? 'y' : 'ies'}`}
              </span>
              {schemaQuery.isFetching && <span className="text-tres-status-pending">Loading…</span>}
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-2 py-2">
            {graphs.length === 0 && (
              <p className="px-3 py-2 text-sm text-tres-text-secondary">
                No graph picked, so no schema is loaded. Pick{' '}
                <span className="font-medium">All graphs</span> or any set of named graphs above.
              </p>
            )}
            {schemaQuery.isError && (
              <p className="px-3 py-2 text-sm text-tres-error">
                {(schemaQuery.error as Error)?.message ?? 'Failed to load the schema.'}
              </p>
            )}
            {schemaQuery.isSuccess && classCount === 0 && properties.length === 0 && (
              <p className="px-3 py-2 text-sm text-tres-text-secondary">
                No classes or properties declared in the selected graphs yet.
                {canEdit ? ' Use + Class to start.' : ''}
              </p>
            )}

            {/* Classes */}
            {filteredForest.length > 0 && (
              <>
                <SectionHeader>Classes</SectionHeader>
                <ul className="text-sm">
                  {shownRoots.shown.map((node) => (
                    <ClassRow
                      key={node.cls.uri}
                      node={node}
                      depth={0}
                      classColors={classColors}
                      expanded={expanded}
                      onToggle={toggleNode}
                      selected={selected}
                      onSelect={(uri) => setSelected({ kind: 'class', uri })}
                      needle={needle}
                    />
                  ))}
                </ul>
                <ShowMoreButton hidden={shownRoots.hidden} what="top classes" onClick={shownRoots.showMore} />
              </>
            )}

            {/* Properties */}
            {filteredProperties.length > 0 && (
              <>
                <SectionHeader>Properties</SectionHeader>
                <ul className="text-sm">
                  {shownProperties.shown.map((p) => (
                    <li key={p.uri}>
                      <div
                        data-term={p.uri}
                        className={cn(
                          'group flex items-center gap-1 px-2 py-1 text-tres-text-primary hover:bg-muted',
                          selected?.kind === 'property' &&
                            selected.uri === p.uri &&
                            'bg-tres-submenu-bg',
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => setSelected({ kind: 'property', uri: p.uri })}
                          className="min-w-0 flex-1 text-left"
                        >
                          <span className="flex items-center gap-2">
                            <span className="hover:underline">
                              <Highlighted text={labelOf(p)} needle={needle} />
                            </span>
                            <KindBadge kind={p.kind} />
                          </span>
                          <TermUri uri={p.uri} needle={needle} />
                        </button>
                        <CopyUriButton uri={p.uri} className="opacity-0 group-hover:opacity-100" />
                      </div>
                    </li>
                  ))}
                </ul>
                <ShowMoreButton hidden={shownProperties.hidden} what="properties" onClick={shownProperties.showMore} />
              </>
            )}
          </div>
        </div>

        {/* Right: detail */}
        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border px-4 py-2 text-xs text-tres-text-secondary">
            {selected?.kind === 'property' ? 'Property detail' : 'Class detail'}
          </div>
          <div className="px-5 py-4">
            {selected && termHomeGraphs.length > 1 && (
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-tres-text-secondary">
                <span>
                  Declared in {termHomeGraphs.length} graphs; this shows what lives in the write
                  target:
                </span>
                {termHomeGraphs.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => chooseWriteGraph(g)}
                    className={cn(
                      'rounded-full border px-2 py-0.5',
                      g === writeGraph
                        ? 'border-tres-action bg-tres-action/10 text-tres-action'
                        : 'border-border hover:bg-muted',
                    )}
                    title={g}
                  >
                    {graphDisplayName(g)}
                  </button>
                ))}
              </div>
            )}
            {!selected && (
              <p className="text-sm text-tres-text-secondary">
                Select a class or property on the left to see its details.
              </p>
            )}
            {selected?.kind === 'class' && !selectedClass && (
              <p className="text-sm text-tres-text-secondary">This class is no longer present.</p>
            )}
            {selected?.kind === 'property' && !selectedProperty && (
              <p className="text-sm text-tres-text-secondary">
                This property is no longer present.
              </p>
            )}
            {writeGraph && selectedClass && schema && (
              <ClassDetailPanel
                key={selectedClass.uri}
                cls={selectedClass}
                schema={schema}
                graph={writeGraph}
                canEdit={canEdit}
                labelMap={labelMap}
                mergeOptions={mergeClassOptions}
                identitySchema={unionSchemaQuery.data ?? schema}
                onSelect={setSelected}
                onDeleted={() => setSelected(null)}
              />
            )}
            {writeGraph && selectedProperty && schema && (
              <PropertyDetailPanel
                key={selectedProperty.uri}
                prop={selectedProperty}
                schema={schema}
                graph={writeGraph}
                canEdit={canEdit}
                labelMap={labelMap}
                mergeOptions={mergePropertyOptions}
                identitySchema={unionSchemaQuery.data ?? schema}
                onSelect={setSelected}
                onDeleted={() => setSelected(null)}
              />
            )}
            {canEdit && writeGraph && selected && detailEntity.data && token && (
              <div className="mt-4">
                <AltLabelsEditor
                  graph={writeGraph}
                  subject={selected.uri}
                  current={detailEntity.data.labels.alt_labels ?? []}
                  predicate={qros ? QROS_ALT_LABEL : SKOS_ALT_LABEL}
                  token={token}
                  onDone={() => {
                    invalidateAfterEdit(queryClient, writeGraph);
                  }}
                />
              </div>
            )}
            {qros && selected && detailEntity.data && (
              <div className="mt-6 border-t border-border pt-4">
                <EntityView
                  data={detailEntity.data}
                  graph={writeGraph}
                  embedded
                  onNavigate={openTerm}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {writeGraph && classModalOpen && (
        <NewClassModal
          graph={writeGraph}
          classes={schema?.classes ?? []}
          labelMap={labelMap}
          showTags={qros}
          identitySchema={unionSchemaQuery.data ?? schema}
          onClose={() => setClassModalOpen(false)}
          onCreated={(uri) => {
            setClassModalOpen(false);
            setExpanded((prev) => new Set([...prev, uri]));
            setSelected({ kind: 'class', uri });
          }}
        />
      )}
      {writeGraph && propertyModalOpen && (
        <NewPropertyModal
          graph={writeGraph}
          classes={schema?.classes ?? []}
          labelMap={labelMap}
          showTags={qros}
          identitySchema={unionSchemaQuery.data ?? schema}
          onClose={() => setPropertyModalOpen(false)}
          onCreated={(uri) => {
            setPropertyModalOpen(false);
            setSelected({ kind: 'property', uri });
          }}
        />
      )}
      {writeGraph && tagModalOpen && (
        <NewTagModal graph={writeGraph} onClose={() => setTagModalOpen(false)} />
      )}
    </div>
  );
}

// ===============================================================
// Left-panel rows
// ===============================================================

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-tres-text-secondary">
      {children}
    </div>
  );
}

// The structural vocabulary the backend actually uses: QROS-native (`:Class`,
// `:ObjectProperty`, `:DatatypeProperty`) on a QROS instance, OWL on RDF. The
// editor surfaces what's stored, not a hardcoded OWL label.
function classTypeLabel(qros: boolean): string {
  return qros ? ':Class' : 'owl:Class';
}
function propertyTypeLabel(qros: boolean, kind: PropertyKind): string {
  if (qros) return kind === 'object' ? ':ObjectProperty' : ':DatatypeProperty';
  return kind === 'object' ? 'owl:ObjectProperty' : 'owl:DatatypeProperty';
}

function KindBadge({ kind }: { kind: PropertyKind }) {
  const qros = isQros(useMe().data);
  return (
    <span
      className="rounded bg-muted px-1 py-0 text-[10px] font-medium uppercase text-tres-text-secondary"
      title={propertyTypeLabel(qros, kind)}
    >
      {kind === 'object' ? 'obj' : 'data'}
    </span>
  );
}

interface ClassRowProps {
  node: ClassTreeNode;
  depth: number;
  /** Each class's colour from the index, the one the Viz canvas draws. */
  classColors: Map<string, VdbClassColor> | undefined;
  expanded: Set<string>;
  onToggle: (uri: string) => void;
  selected: Selection;
  onSelect: (uri: string) => void;
  needle: string;
}

function ClassRow({
  node,
  depth,
  classColors,
  expanded,
  onToggle,
  selected,
  onSelect,
  needle,
}: ClassRowProps) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.cls.uri);
  const shownChildren = useShownRows(node.children);
  const isSelected = selected?.kind === 'class' && selected.uri === node.cls.uri;
  const indents = ['', 'pl-6', 'pl-10', 'pl-14', 'pl-[4.5rem]', 'pl-[5.5rem]', 'pl-24', 'pl-28'];
  const indentClass = indents[Math.min(depth, indents.length - 1)] ?? 'pl-28';
  const family = classColors?.get(node.cls.uri);
  const dotColor = family ? colorByIndex(family.color_index, family.depth) : null;
  const familyBase = baseClassForSlot(family?.color_index);

  return (
    <li>
      <div
        data-term={node.cls.uri}
        className={cn(
          'group flex items-center gap-1 py-1 pr-2 text-tres-text-primary hover:bg-muted',
          indentClass,
          isSelected && 'bg-tres-submenu-bg',
        )}
      >
        <button
          type="button"
          onClick={() => (hasChildren ? onToggle(node.cls.uri) : undefined)}
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'flex h-5 w-5 items-center justify-center')}
          aria-label={hasChildren ? (isOpen ? 'Collapse' : 'Expand') : undefined}
        >
          {hasChildren ? <Caret open={isOpen} /> : <span className="text-xs">·</span>}
        </button>
        {dotColor && (
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: dotColor }}
            title={familyBase ? `base class: ${familyBase}` : undefined}
            aria-hidden
          />
        )}
        <button
          type="button"
          onClick={() => onSelect(node.cls.uri)}
          className="min-w-0 flex-1 text-left"
        >
          <span className="hover:underline">
            <Highlighted text={labelOf(node.cls)} needle={needle} />
          </span>
          <TermUri uri={node.cls.uri} needle={needle} />
        </button>
        <CopyUriButton uri={node.cls.uri} className="opacity-0 group-hover:opacity-100" />
      </div>
      {isOpen && hasChildren && (
        <ul>
          {shownChildren.shown.map((child) => (
            <ClassRow
              key={child.cls.uri}
              node={child}
              depth={depth + 1}
              classColors={classColors}
              expanded={expanded}
              onToggle={onToggle}
              selected={selected}
              onSelect={onSelect}
              needle={needle}
            />
          ))}
          {shownChildren.hidden > 0 && (
            <li className={indents[Math.min(depth + 1, indents.length - 1)]}>
              <ShowMoreButton
                hidden={shownChildren.hidden}
                what="subclasses"
                onClick={shownChildren.showMore}
              />
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

// ===============================================================
// Class detail + edit
// ===============================================================

interface ClassDetailPanelProps {
  cls: SchemaClass;
  schema: OntologySchema;
  graph: string;
  canEdit: boolean;
  labelMap: Map<string, string>;
  mergeOptions: { uri: string; label: string }[];
  /** Store-wide schema, for the rename form's namespace choices. */
  identitySchema: OntologySchema | undefined;
  onSelect: (sel: Selection) => void;
  onDeleted: () => void;
}

function ClassDetailPanel({
  cls,
  schema,
  graph,
  canEdit,
  labelMap,
  mergeOptions,
  identitySchema,
  onSelect,
  onDeleted,
}: ClassDetailPanelProps) {
  const classColors = useVdbClassColors().data;
  const auth = useAuth();
  const me = useMe();
  const queryClient = useQueryClient();
  const token = auth.user?.id_token;
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState('');
  const [annotations, setAnnotations] = useState<AnnotationValue[]>([]);
  const [supers, setSupers] = useState<string[]>([]);
  const [tags, setTags] = useState<TagDraft>(EMPTY_TAG_DRAFT);
  const [mergeTarget, setMergeTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const qros = isQros(me.data);
  const renameNamespaces = useNamespaceOptions(identitySchema, token);
  const renameTaken = useTakenUris(identitySchema);

  // Only in-schema parents are editable; an external parent (owl:Thing,
  // an imported class) is shown read-only so an edit never silently drops it.
  const inSchemaUris = useMemo(() => new Set(schema.classes.map((c) => c.uri)), [schema]);
  const externalSupers = cls.super_classes.filter((s) => !inSchemaUris.has(s));
  const subclasses = useMemo(
    () => schema.classes.filter((c) => c.super_classes.includes(cls.uri)),
    [schema, cls.uri],
  );
  const props = useMemo(
    () => propertiesForClass(schema.properties, cls.uri),
    [schema.properties, cls.uri],
  );
  const candidateParents = schema.classes.filter((c) => c.uri !== cls.uri);

  function startEdit() {
    setError(null);
    setLabel(labelOf(cls));
    setAnnotations(cls.annotations.map((a) => ({ ...a })));
    setSupers(cls.super_classes.filter((s) => inSchemaUris.has(s)));
    setTags(EMPTY_TAG_DRAFT);
    setEditing(true);
  }

  async function save() {
    if (!token) return;
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Label cannot be empty.');
      return;
    }
    const pendingContext = unpickedContext(tags);
    if (pendingContext) {
      setError(pendingContext);
      return;
    }
    const body: PatchClassRequest = { graph, uri: cls.uri };
    if (trimmed !== labelOf(cls)) body.label = trimmed;
    const nextAnnotations = normalizedAnnotations(annotations);
    if (!sameAnnotations(nextAnnotations, cls.annotations)) body.annotations = nextAnnotations;
    const currentInSchema = cls.super_classes.filter((s) => inSchemaUris.has(s));
    if (!sameSet(supers, currentInSchema)) {
      // Replacement set must preserve external parents the editor can't pick.
      body.super_classes = [...supers, ...externalSupers];
    }
    // Tags ride on the rewritten statements; a tags-only save reseals the
    // definition (the backend handles the no-field-change case).
    const tagList = qros ? serializeTags(tags) : [];
    if (tagList.length) body.tags = tagList;
    if (
      body.label === undefined &&
      body.annotations === undefined &&
      body.super_classes === undefined &&
      !tagList.length
    ) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    const result = await patchClass(body, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, graph);
      setEditing(false);
    } else {
      setError(result.message);
    }
    setBusy(false);
  }

  async function handleDelete() {
    if (!token) return;
    setBusy(true);
    setError(null);
    const result = await deleteClass(graph, cls.uri, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, graph);
      onDeleted();
    } else {
      setError(result.message);
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  async function handleMerge() {
    if (!token || !mergeTarget) return;
    setBusy(true);
    setError(null);
    const result = await mergeEntity({ graph, source: cls.uri, target: mergeTarget }, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, graph);
      onSelect({ kind: 'class', uri: mergeTarget });
    } else {
      setError(result.message);
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <EditForm
        title="Edit class"
        busy={busy}
        error={error}
        confirmingDelete={confirmingDelete}
        deleteLabel={labelOf(cls)}
        uri={cls.uri}
        onCancel={() => setEditing(false)}
        onSave={() => void save()}
        onAskDelete={() => {
          setError(null);
          setConfirmingDelete(true);
        }}
        onCancelDelete={() => setConfirmingDelete(false)}
        onConfirmDelete={() => void handleDelete()}
      >
        <LabelField value={label} onChange={setLabel} disabled={busy} />
        <AnnotationListEditor
          value={annotations}
          onChange={setAnnotations}
          predicates={TERM_ANNOTATION_PREDICATES}
          disabled={busy}
        />
        <ClassMultiSelect
          title="Subclass of"
          selected={supers}
          options={candidateParents}
          labelMap={labelMap}
          disabled={busy}
          onChange={setSupers}
        />
        {externalSupers.length > 0 && (
          <p className="text-xs text-tres-text-secondary">
            Also a subclass of {externalSupers.map((s) => labelMap.get(s) ?? localName(s)).join(', ')}{' '}
            (kept as-is).
          </p>
        )}
        {qros && (
          <TagEditor value={tags} onChange={setTags} label="Tags for this class" graph={graph} />
        )}
        {qros && (
          <RenameControl
            kind="class"
            graph={graph}
            uri={cls.uri}
            namespaces={renameNamespaces}
            collides={renameTaken}
            onRenamed={(next) => {
              setEditing(false);
              onSelect({ kind: 'class', uri: next });
            }}
          />
        )}
        {qros && (
          <MergeIntoControl
            kind="class"
            busy={busy}
            target={mergeTarget}
            options={mergeOptions.filter((o) => o.uri !== cls.uri)}
            onTarget={setMergeTarget}
            onMerge={() => void handleMerge()}
          />
        )}
      </EditForm>
    );
  }

  // A base class is the foundation — immutable in the editor (no edit, no
  // delete). Other classes anchor TO it; you don't redefine it.
  const isBase = baseClassLocalName(cls.uri) !== null;

  return (
    <div className="space-y-4">
      <DetailHeader
        title={labelOf(cls)}
        uri={cls.uri}
        canEdit={canEdit && !isBase}
        onEdit={startEdit}
        badge={classTypeLabel(isQros(me.data))}
        {...(() => {
          const family = classColors?.get(cls.uri);
          const c = family ? colorByIndex(family.color_index, family.depth) : null;
          return c ? { swatchColor: c } : {};
        })()}
      />
      {error && <ErrorBox message={error} />}
      {isBase && (
        <p className="text-xs italic text-tres-text-secondary">
          Foundation base class — the anchor for this family. Classes and instances
          attach to it; it can't be edited or deleted.
        </p>
      )}
      <DocumentationSections annotations={cls.annotations} />
      {cls.super_classes.length > 0 && (
        <DetailSection title="Subclass of">
          <UriList
            uris={cls.super_classes}
            labelMap={labelMap}
            inSchema={inSchemaUris}
            onSelect={(uri) => onSelect({ kind: 'class', uri })}
          />
        </DetailSection>
      )}
      {subclasses.length > 0 && (
        <DetailSection title="Subclasses">
          <UriList
            uris={subclasses.map((c) => c.uri)}
            labelMap={labelMap}
            inSchema={inSchemaUris}
            onSelect={(uri) => onSelect({ kind: 'class', uri })}
          />
        </DetailSection>
      )}
      {props.length > 0 && (
        <DetailSection title="Properties on this class">
          <ul className="space-y-0.5 text-sm">
            {props.map((p) => (
              <li key={p.uri} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onSelect({ kind: 'property', uri: p.uri })}
                  className={cn(buttonVariants({ variant: 'link', size: 'md' }), 'text-left')}
                >
                  {labelOf(p)}
                </button>
                <KindBadge kind={p.kind} />
              </li>
            ))}
          </ul>
        </DetailSection>
      )}
      {qros && canEdit && !isBase && (
        <DetailSection title="Cannot also be">
          <DisjointRuleForm cls={cls} classes={schema.classes} graph={graph} />
        </DetailSection>
      )}
    </div>
  );
}

// ===============================================================
// Property detail + edit
// ===============================================================

interface PropertyDetailPanelProps {
  prop: SchemaProperty;
  schema: OntologySchema;
  graph: string;
  canEdit: boolean;
  labelMap: Map<string, string>;
  mergeOptions: { uri: string; label: string }[];
  /** See {@link ClassDetailPanelProps.identitySchema}. */
  identitySchema: OntologySchema | undefined;
  onSelect: (sel: Selection) => void;
  onDeleted: () => void;
}

function PropertyDetailPanel({
  prop,
  schema,
  graph,
  canEdit,
  labelMap,
  mergeOptions,
  identitySchema,
  onSelect,
  onDeleted,
}: PropertyDetailPanelProps) {
  const auth = useAuth();
  const me = useMe();
  const queryClient = useQueryClient();
  const token = auth.user?.id_token;
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState('');
  const [annotations, setAnnotations] = useState<AnnotationValue[]>([]);
  const [kind, setKind] = useState<PropertyKind>('object');
  const [domain, setDomain] = useState<string[]>([]);
  const [range, setRange] = useState<string[]>([]);
  const [tags, setTags] = useState<TagDraft>(EMPTY_TAG_DRAFT);
  const [mergeTarget, setMergeTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const qros = isQros(me.data);
  const renameNamespaces = useNamespaceOptions(identitySchema, token);
  const renameTaken = useTakenUris(identitySchema);

  const inSchemaUris = useMemo(() => new Set(schema.classes.map((c) => c.uri)), [schema]);
  const classOptions = schema.classes;
  // External (non-class) domain entries kept as-is on save.
  const externalDomain = prop.domain.filter((d) => !inSchemaUris.has(d));

  function startEdit() {
    setError(null);
    setLabel(labelOf(prop));
    setAnnotations(prop.annotations.map((a) => ({ ...a })));
    setKind(prop.kind);
    setDomain(prop.domain.filter((d) => inSchemaUris.has(d)));
    setRange(prop.range);
    setTags(EMPTY_TAG_DRAFT);
    setEditing(true);
  }

  async function save() {
    if (!token) return;
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Label cannot be empty.');
      return;
    }
    const pendingContext = unpickedContext(tags);
    if (pendingContext) {
      setError(pendingContext);
      return;
    }
    const body: PatchPropertyRequest = { graph, uri: prop.uri };
    if (trimmed !== labelOf(prop)) body.label = trimmed;
    const nextAnnotations = normalizedAnnotations(annotations);
    if (!sameAnnotations(nextAnnotations, prop.annotations)) body.annotations = nextAnnotations;
    if (kind !== prop.kind) body.kind = kind;
    const currentDomainInSchema = prop.domain.filter((d) => inSchemaUris.has(d));
    if (!sameSet(domain, currentDomainInSchema)) {
      body.domain = [...domain, ...externalDomain];
    }
    if (!sameSet(range, prop.range)) body.range = range;
    // Tags ride on the rewritten statements; a tags-only save reseals the
    // definition (the backend handles the no-field-change case).
    const tagList = qros ? serializeTags(tags) : [];
    if (tagList.length) body.tags = tagList;
    if (
      body.label === undefined &&
      body.annotations === undefined &&
      body.kind === undefined &&
      body.domain === undefined &&
      body.range === undefined &&
      !tagList.length
    ) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    const result = await patchProperty(body, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, graph);
      setEditing(false);
    } else {
      setError(result.message);
    }
    setBusy(false);
  }

  async function handleDelete() {
    if (!token) return;
    setBusy(true);
    setError(null);
    const result = await deleteProperty(graph, prop.uri, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, graph);
      onDeleted();
    } else {
      setError(result.message);
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  async function handleMerge() {
    if (!token || !mergeTarget) return;
    setBusy(true);
    setError(null);
    const result = await mergeEntity({ graph, source: prop.uri, target: mergeTarget }, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, graph);
      onSelect({ kind: 'property', uri: mergeTarget });
    } else {
      setError(result.message);
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <EditForm
        title="Edit property"
        busy={busy}
        error={error}
        confirmingDelete={confirmingDelete}
        deleteLabel={labelOf(prop)}
        uri={prop.uri}
        onCancel={() => setEditing(false)}
        onSave={() => void save()}
        onAskDelete={() => {
          setError(null);
          setConfirmingDelete(true);
        }}
        onCancelDelete={() => setConfirmingDelete(false)}
        onConfirmDelete={() => void handleDelete()}
      >
        <LabelField value={label} onChange={setLabel} disabled={busy} />
        <AnnotationListEditor
          value={annotations}
          onChange={setAnnotations}
          predicates={TERM_ANNOTATION_PREDICATES}
          disabled={busy}
        />
        <KindField
          value={kind}
          disabled={busy}
          onChange={(k) => {
            setKind(k);
            // Range vocabulary differs by kind; reset to avoid keeping an
            // xsd datatype as an object-property range or vice versa.
            setRange([]);
          }}
        />
        <ClassMultiSelect
          title="Domain"
          selected={domain}
          options={classOptions}
          labelMap={labelMap}
          disabled={busy}
          onChange={setDomain}
        />
        <RangeEditor
          kind={kind}
          selected={range}
          classOptions={classOptions}
          labelMap={labelMap}
          disabled={busy}
          onChange={setRange}
        />
        {externalDomain.length > 0 && (
          <p className="text-xs text-tres-text-secondary">
            Domain also includes{' '}
            {externalDomain.map((d) => labelMap.get(d) ?? localName(d)).join(', ')} (kept as-is).
          </p>
        )}
        {qros && (
          <TagEditor value={tags} onChange={setTags} label="Tags for this property" graph={graph} />
        )}
        {qros && (
          <RenameControl
            kind="property"
            graph={graph}
            uri={prop.uri}
            namespaces={renameNamespaces}
            collides={renameTaken}
            onRenamed={(next) => {
              setEditing(false);
              onSelect({ kind: 'property', uri: next });
            }}
          />
        )}
        {qros && (
          <MergeIntoControl
            kind="property"
            busy={busy}
            target={mergeTarget}
            options={mergeOptions.filter((o) => o.uri !== prop.uri)}
            onTarget={setMergeTarget}
            onMerge={() => void handleMerge()}
          />
        )}
      </EditForm>
    );
  }

  return (
    <div className="space-y-4">
      <DetailHeader
        title={labelOf(prop)}
        uri={prop.uri}
        canEdit={canEdit}
        onEdit={startEdit}
        badge={propertyTypeLabel(isQros(me.data), prop.kind)}
      />
      {error && <ErrorBox message={error} />}
      <DocumentationSections annotations={prop.annotations} />
      <DetailSection title="Domain">
        {prop.domain.length > 0 ? (
          <UriList
            uris={prop.domain}
            labelMap={labelMap}
            inSchema={inSchemaUris}
            onSelect={(uri) => onSelect({ kind: 'class', uri })}
          />
        ) : (
          <p className="text-sm italic text-tres-text-secondary">Any (unspecified).</p>
        )}
      </DetailSection>
      <DetailSection title="Range">
        {prop.range.length > 0 ? (
          <UriList
            uris={prop.range}
            labelMap={labelMap}
            inSchema={inSchemaUris}
            onSelect={(uri) => onSelect({ kind: 'class', uri })}
          />
        ) : (
          <p className="text-sm italic text-tres-text-secondary">Any (unspecified).</p>
        )}
      </DetailSection>
    </div>
  );
}

// ===============================================================
// Create modals
// ===============================================================

interface NewClassModalProps {
  graph: string;
  classes: SchemaClass[];
  labelMap: Map<string, string>;
  showTags: boolean;
  /** Store-wide schema — the namespace default and the duplicate pre-check
   *  have to see every declared term, not just the selected graph's. */
  identitySchema: OntologySchema | undefined;
  onClose: () => void;
  onCreated: (uri: string) => void;
}

function NewClassModal({
  graph,
  classes,
  labelMap,
  showTags,
  identitySchema,
  onClose,
  onCreated,
}: NewClassModalProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const token = auth.user?.id_token;
  const [label, setLabel] = useState('');
  const [comment, setComment] = useState('');
  const [supers, setSupers] = useState<string[]>([]);
  const [tags, setTags] = useState<TagDraft>(EMPTY_TAG_DRAFT);
  const [identity, setIdentity] = useState<IdentityDraft>(EMPTY_IDENTITY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const namespaces = useNamespaceOptions(identitySchema, token);
  const defaultNamespace = deriveSchemaNamespace(identitySchema);
  const taken = useTakenUris(identitySchema);
  const uri = draftUri(identity, defaultNamespace, slugifyLocalName(label, 'class'));

  async function submit() {
    if (!token) return;
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Label is required.');
      return;
    }
    if (!uri) {
      setError('The label produces no URI — give it letters or digits, or set a local name.');
      return;
    }
    if (taken(uri)) {
      setError(`${uri} already exists. Pick a different local name or namespace.`);
      return;
    }
    const pendingContext = unpickedContext(tags);
    if (pendingContext) {
      setError(pendingContext);
      return;
    }
    setBusy(true);
    setError(null);
    // The URI is sent explicitly, always: what the form previewed is exactly
    // what gets minted, whether or not the author edited it.
    const body: CreateClassRequest = { graph, label: trimmed, super_classes: supers, uri };
    const c = comment.trim();
    if (c) body.comment = c;
    const tagList = showTags ? serializeTags(tags) : [];
    if (tagList.length) body.tags = tagList;
    const result = await createClass(body, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, graph);
      onCreated(result.data.uri);
    } else {
      setError(result.message);
      setBusy(false);
    }
  }

  return (
    <ModalShell title="New class" busy={busy} onClose={onClose} onSubmit={() => void submit()}>
      <LabelField value={label} onChange={setLabel} disabled={busy} autoFocus />
      <IdentityFields
        kind="class"
        label={label}
        draft={identity}
        onChange={setIdentity}
        namespaces={namespaces}
        defaultNamespace={defaultNamespace}
        disabled={busy}
        collides={taken}
        resetLabel="Back to the derived URI"
      />
      <CommentField value={comment} onChange={setComment} disabled={busy} />
      <ClassMultiSelect
        title="Subclass of"
        selected={supers}
        options={classes}
        labelMap={labelMap}
        disabled={busy}
        onChange={setSupers}
      />
      {showTags && (
        <TagEditor value={tags} onChange={setTags} label="Tags for this class" graph={graph} />
      )}
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
}

interface NewPropertyModalProps {
  graph: string;
  classes: SchemaClass[];
  labelMap: Map<string, string>;
  showTags: boolean;
  /** See {@link NewClassModalProps.identitySchema}. */
  identitySchema: OntologySchema | undefined;
  onClose: () => void;
  onCreated: (uri: string) => void;
}

function NewPropertyModal({
  graph,
  classes,
  labelMap,
  showTags,
  identitySchema,
  onClose,
  onCreated,
}: NewPropertyModalProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const token = auth.user?.id_token;
  const [label, setLabel] = useState('');
  const [comment, setComment] = useState('');
  const [kind, setKind] = useState<PropertyKind>('object');
  const [domain, setDomain] = useState<string[]>([]);
  const [range, setRange] = useState<string[]>([]);
  const [tags, setTags] = useState<TagDraft>(EMPTY_TAG_DRAFT);
  const [identity, setIdentity] = useState<IdentityDraft>(EMPTY_IDENTITY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const namespaces = useNamespaceOptions(identitySchema, token);
  const defaultNamespace = deriveSchemaNamespace(identitySchema);
  const taken = useTakenUris(identitySchema);
  const uri = draftUri(identity, defaultNamespace, slugifyLocalName(label, 'property'));

  async function submit() {
    if (!token) return;
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Label is required.');
      return;
    }
    if (!uri) {
      setError('The label produces no URI — give it letters or digits, or set a local name.');
      return;
    }
    if (taken(uri)) {
      setError(`${uri} already exists. Pick a different local name or namespace.`);
      return;
    }
    const pendingContext = unpickedContext(tags);
    if (pendingContext) {
      setError(pendingContext);
      return;
    }
    setBusy(true);
    setError(null);
    const body: CreatePropertyRequest = { graph, label: trimmed, kind, domain, range, uri };
    const c = comment.trim();
    if (c) body.comment = c;
    const tagList = showTags ? serializeTags(tags) : [];
    if (tagList.length) body.tags = tagList;
    const result = await createProperty(body, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, graph);
      onCreated(result.data.uri);
    } else {
      setError(result.message);
      setBusy(false);
    }
  }

  return (
    <ModalShell title="New property" busy={busy} onClose={onClose} onSubmit={() => void submit()}>
      <LabelField value={label} onChange={setLabel} disabled={busy} autoFocus />
      <IdentityFields
        kind="property"
        label={label}
        draft={identity}
        onChange={setIdentity}
        namespaces={namespaces}
        defaultNamespace={defaultNamespace}
        disabled={busy}
        collides={taken}
        resetLabel="Back to the derived URI"
      />
      <CommentField value={comment} onChange={setComment} disabled={busy} />
      <KindField
        value={kind}
        disabled={busy}
        onChange={(k) => {
          setKind(k);
          setRange([]);
        }}
      />
      <ClassMultiSelect
        title="Domain"
        selected={domain}
        options={classes}
        labelMap={labelMap}
        disabled={busy}
        onChange={setDomain}
      />
      <RangeEditor
        kind={kind}
        selected={range}
        classOptions={classes}
        labelMap={labelMap}
        disabled={busy}
        onChange={setRange}
      />
      {showTags && (
        <TagEditor value={tags} onChange={setTags} label="Tags for this property" graph={graph} />
      )}
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
}

const TAG_VALUE_KINDS: { value: string; label: string }[] = [
  { value: 'text', label: 'text' },
  { value: 'number', label: 'number' },
  { value: 'date', label: 'date' },
  { value: 'entity', label: 'entity reference' },
  { value: 'flag', label: 'flag (true/false)' },
];

/** Declare a reusable statement tag: after creating, `@name` appears in every
 *  tag editor's catalog dropdown (QROS-native). */
function NewTagModal({ graph, onClose }: { graph: string; onClose: () => void }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const token = auth.user?.id_token;
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('text');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!token) return;
    const trimmed = name.trim().replace(/^@/, '');
    if (!trimmed) {
      setError('Tag name is required.');
      return;
    }
    setBusy(true);
    setError(null);
    const body: Parameters<typeof createTag>[0] = { graph, name: trimmed, value_kind: kind };
    const l = label.trim();
    if (l) body.label = l;
    const result = await createTag(body, token);
    if (result.ok) {
      void queryClient.invalidateQueries({ queryKey: ['tags', 'catalog'] });
      onClose();
    } else {
      setError(result.message);
      setBusy(false);
    }
  }

  return (
    <ModalShell title="New tag" busy={busy} onClose={onClose} onSubmit={() => void submit()}>
      <label className="block">
        <span className="text-sm font-medium text-tres-text-primary">
          Tag name <span className="text-tres-error">*</span>
        </span>
        <input
          className={inputClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. reviewCycle — used as @reviewCycle on statements"
          disabled={busy}
          autoFocus
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-tres-text-primary">Label</span>
        <input
          className={inputClass}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Human-readable name (defaults to the tag name)"
          disabled={busy}
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-tres-text-primary">Value kind</span>
        <select
          className={inputClass}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          disabled={busy}
        >
          {TAG_VALUE_KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </label>
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
}

// ===============================================================
// Identity control — namespace + local name, live URI, rename
// ===============================================================

/** The two halves of a term's identity as the author is editing them. `null`
 *  means "still following the default" (derived namespace / slugified label);
 *  a string is the author's own choice. */
interface IdentityDraft {
  namespace: string | null;
  local: string | null;
}

const EMPTY_IDENTITY: IdentityDraft = { namespace: null, local: null };

/** Resolve a draft against the defaults into the URI that will actually be
 *  written. Empty local name = no URI yet (the label has no usable slug). */
function draftUri(draft: IdentityDraft, defaultNamespace: string, defaultLocal: string): string {
  const namespace = draft.namespace ?? defaultNamespace;
  const local = (draft.local ?? defaultLocal).trim();
  return local ? `${namespace}${local}` : '';
}

/** Every URI the schema already uses as a term — the client-side duplicate
 *  pre-check, so a collision is visible on the form before the round trip.
 *  The server's refusal remains authoritative (it sees the whole store). */
function useTakenUris(schema: OntologySchema | undefined): (uri: string) => boolean {
  const taken = useMemo(() => {
    const set = new Set<string>();
    for (const c of schema?.classes ?? []) set.add(c.uri);
    for (const p of schema?.properties ?? []) set.add(p.uri);
    return set;
  }, [schema]);
  return (uri: string) => taken.has(uri);
}

/** Namespaces an author may mint into: the ones this schema already uses,
 *  plus every URI in the instance's prefix registry (Preferences → Namespace
 *  prefixes). The schema's own derived default is always first. */
function useNamespaceOptions(
  schema: OntologySchema | undefined,
  token: string | undefined,
): { uri: string; label: string }[] {
  const registry = useQuery<NamespaceList, Error>({
    queryKey: ['namespaces'],
    enabled: !!token,
    queryFn: async () => {
      if (!token) throw new Error('no token');
      const r = await listNamespaces(token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
  return useMemo(() => {
    const seen = new Map<string, string>();
    const add = (uri: string, label: string) => {
      if (uri && !seen.has(uri)) seen.set(uri, label);
    };
    add(deriveSchemaNamespace(schema), 'this schema');
    for (const term of [...(schema?.classes ?? []), ...(schema?.properties ?? [])]) {
      const { namespace, local } = splitUri(term.uri);
      if (namespace && local) add(namespace, 'in use');
    }
    for (const p of registry.data?.prefixes ?? []) add(p.uri, `${p.prefix}:`);
    return [...seen].map(([uri, label]) => ({ uri, label }));
  }, [schema, registry.data]);
}

/** Namespace + local name, with the resulting URI shown live. Auto-derivation
 *  from the label is the DEFAULT — the moment the author edits either field,
 *  their choice sticks and the label stops driving it. */
function IdentityFields({
  kind,
  label,
  draft,
  onChange,
  namespaces,
  defaultNamespace,
  disabled,
  collides,
  resetLabel,
}: {
  kind: 'class' | 'property';
  label: string;
  draft: IdentityDraft;
  onChange: (next: IdentityDraft) => void;
  namespaces: { uri: string; label: string }[];
  defaultNamespace: string;
  disabled: boolean;
  collides: (uri: string) => boolean;
  /** Text of the "undo my edits" link. */
  resetLabel: string;
}) {
  const defaultLocal = slugifyLocalName(label, kind);
  const namespace = draft.namespace ?? defaultNamespace;
  const local = draft.local ?? defaultLocal;
  const uri = draftUri(draft, defaultNamespace, defaultLocal);
  const known = namespaces.some((n) => n.uri === namespace);
  const [custom, setCustom] = useState(false);
  const showCustom = custom || !known;

  return (
    <div className="mb-3 rounded-md border border-border bg-muted/60 p-2">
      <span className="text-sm font-medium text-tres-text-primary">Identity (URI)</span>
      <p className="mt-0.5 text-xs text-tres-text-secondary">
        Derived from the label. Edit either part to choose your own — this is the token you
        will type in rules and queries.
      </p>
      <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs text-tres-text-secondary">Namespace</span>
          {showCustom ? (
            <input
              type="text"
              value={namespace}
              onChange={(e) => onChange({ ...draft, namespace: e.target.value })}
              disabled={disabled}
              className={`${inputClass} font-mono text-xs`}
              placeholder="http://example.org/ or :"
            />
          ) : (
            <select
              value={namespace}
              disabled={disabled}
              onChange={(e) => {
                if (e.target.value === CUSTOM_NAMESPACE) {
                  setCustom(true);
                  return;
                }
                onChange({ ...draft, namespace: e.target.value });
              }}
              className={`${inputClass} font-mono text-xs`}
            >
              {namespaces.map((n) => (
                <option key={n.uri} value={n.uri}>
                  {n.uri} ({n.label})
                </option>
              ))}
              <option value={CUSTOM_NAMESPACE}>Other — type it…</option>
            </select>
          )}
          {showCustom && namespaces.length > 0 && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setCustom(false);
                onChange({ ...draft, namespace: null });
              }}
              className={cn(buttonVariants({ variant: 'link', size: 'sm' }), 'mt-1')}
            >
              Choose from the registry
            </button>
          )}
        </label>
        <label className="block">
          <span className="text-xs text-tres-text-secondary">Local name</span>
          <input
            type="text"
            value={local}
            onChange={(e) => onChange({ ...draft, local: e.target.value })}
            disabled={disabled}
            maxLength={200}
            className={`${inputClass} font-mono text-xs`}
          />
        </label>
      </div>
      <p className="mt-1.5 select-text break-all font-mono text-xs text-tres-text-primary">
        {uri || <span className="italic text-tres-text-secondary">no URI yet — add a label</span>}
      </p>
      {uri && collides(uri) && (
        <p className="mt-1 text-xs text-tres-error">
          {uri} already exists. Pick a different local name or namespace.
        </p>
      )}
      {(draft.namespace !== null || draft.local !== null) && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(EMPTY_IDENTITY)}
          className={cn(buttonVariants({ variant: 'link', size: 'sm' }), 'mt-1')}
        >
          {resetLabel}
        </button>
      )}
    </div>
  );
}

const CUSTOM_NAMESPACE = '__custom_namespace__';

/** Rename a term — in QROS the URI IS the identity, so this rewrites every
 *  statement and rule that carries it, atomically, server-side. The blast
 *  radius is shown BEFORE the button does anything: while authoring, nothing
 *  references the term yet and the rename is nearly free. */
function RenameControl({
  kind,
  graph,
  uri,
  namespaces,
  collides,
  onRenamed,
}: {
  kind: 'class' | 'property';
  graph: string;
  uri: string;
  namespaces: { uri: string; label: string }[];
  collides: (uri: string) => boolean;
  onRenamed: (next: string) => void;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const token = auth.user?.id_token;
  const [open, setOpen] = useState(false);
  const current = splitUri(uri);
  const [draft, setDraft] = useState<IdentityDraft>(EMPTY_IDENTITY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refs = useQuery<TermReferences, Error>({
    queryKey: ['ontology', 'references', uri],
    enabled: open && !!token,
    queryFn: async () => {
      if (!token) throw new Error('no token');
      const r = await termReferences(uri, token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });

  const next = draftUri(draft, current.namespace, current.local);
  const unchanged = next === uri;
  // The blast radius is a precondition, not decoration: without it the author
  // would be agreeing to a cascade of unknown size.
  const blocked = !next || unchanged || refs.isError || refs.isLoading || collides(next);

  async function submit() {
    if (!token || !next || unchanged) return;
    setBusy(true);
    setError(null);
    const result = await renameTerm({ graph, uri, new_uri: next }, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, graph);
      void queryClient.invalidateQueries({ queryKey: ['ontology', 'references'] });
      onRenamed(next);
    } else {
      setError(result.message);
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(EMPTY_IDENTITY);
          setError(null);
          setOpen(true);
        }}
        className={buttonVariants({ variant: 'secondary', size: 'md' })}
      >
        Rename (change the URI)…
      </button>
    );
  }

  return (
    <div className="space-y-1.5 rounded-md border border-tres-action/40 bg-tres-submenu-bg/40 p-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-tres-text-secondary">
        Rename this {kind}
      </span>
      <BlastRadius query={refs} />
      <IdentityFields
        kind={kind}
        label=""
        draft={{ namespace: draft.namespace ?? current.namespace, local: draft.local ?? current.local }}
        onChange={setDraft}
        namespaces={namespaces}
        defaultNamespace={current.namespace}
        disabled={busy}
        collides={(candidate) => candidate !== uri && collides(candidate)}
        resetLabel="Back to the current URI"
      />
      {error && <ErrorBox message={error} />}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy || blocked}
          onClick={() => void submit()}
          className={buttonVariants({ variant: 'primary', size: 'md' })}
        >
          {busy ? 'Renaming…' : 'Rename'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setOpen(false)}
          className={buttonVariants({ variant: 'secondary', size: 'md' })}
        >
          Cancel
        </button>
        {unchanged && <span className="text-xs text-tres-text-secondary">Change the URI first.</span>}
      </div>
    </div>
  );
}

/** What a rename will disturb, in one sentence. The cheap case (nothing
 *  points at the term yet) says so plainly. */
function BlastRadius({ query }: { query: UseQueryResult<TermReferences, Error> }) {
  if (query.isLoading) {
    return <p className="text-xs text-tres-text-secondary">Checking what references this…</p>;
  }
  if (query.isError) {
    return (
      <p className="text-xs text-tres-error">
        Could not check references: {query.error.message}. Rename is unavailable until this
        succeeds.
      </p>
    );
  }
  const refs = query.data;
  if (!refs) return null;
  const incoming = incomingReferences(refs);
  const parts: string[] = [];
  if (refs.as_object) parts.push(`${refs.as_object} pointing at it`);
  if (refs.as_predicate) parts.push(`${refs.as_predicate} using it as a predicate`);
  if (refs.in_scope) parts.push(`${refs.in_scope} scoped to it`);
  return (
    <p className="text-xs text-tres-text-secondary">
      {incoming === 0 ? (
        <>
          Nothing references this yet — renaming moves only its own {refs.as_subject} statement
          {refs.as_subject === 1 ? '' : 's'}.
        </>
      ) : (
        <>
          {incoming} statement{incoming === 1 ? '' : 's'} reference{incoming === 1 ? 's' : ''} this
          ({parts.join(', ')}); renaming rewrites them, along with its own {refs.as_subject}.
        </>
      )}
      {refs.rules.length > 0 && (
        <>
          {' '}
          Rules updated: <span className="font-mono">{refs.rules.join(', ')}</span>.
        </>
      )}
    </p>
  );
}

// ===============================================================
// Shared form + presentational bits
// ===============================================================

/** Merge this term into another: every statement and reference moves to the
 *  target (which keeps its own label), then this term disappears. This is how
 *  two terms that turned out to be the same thing get consolidated — to change
 *  one term's URI, use Rename, which keeps it a single term. */
function MergeIntoControl({
  kind,
  busy,
  target,
  options,
  onTarget,
  onMerge,
}: {
  kind: 'class' | 'property';
  busy: boolean;
  target: string;
  options: { uri: string; label: string }[];
  onTarget: (uri: string) => void;
  onMerge: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const targetLabel = options.find((o) => o.uri === target)?.label;
  return (
    <div className={cn(messageClass('platform', 'normal', 'warning'), 'space-y-1.5')}>
      <span className="text-xs font-semibold uppercase tracking-wide text-tres-text-secondary">
        Merge into another {kind}
      </span>
      <p className="text-xs text-tres-text-secondary">
        Moves every statement and reference from this {kind} onto the target (the target keeps its
        own label); this {kind} then disappears. Use it to consolidate two terms that turned out to
        be the same thing — to change this {kind}&rsquo;s URI, use Rename instead.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className={`${inputClass} mt-0 max-w-xs`}
          value={target}
          disabled={busy}
          onChange={(e) => {
            setConfirming(false);
            onTarget(e.target.value);
          }}
        >
          <option value="">Choose the target {kind}…</option>
          {options.map((o) => (
            <option key={o.uri} value={o.uri}>
              {o.label} — {o.uri}
            </option>
          ))}
        </select>
        {!confirming ? (
          <button
            type="button"
            disabled={busy || !target}
            onClick={() => setConfirming(true)}
            className={buttonVariants({ variant: 'warning', size: 'md' })}
          >
            Merge…
          </button>
        ) : (
          <>
            <span className="text-xs text-tres-text-secondary">
              Merge into “{targetLabel}”? This cannot be undone.
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={onMerge}
              className={buttonVariants({ variant: 'warning', size: 'md' })}
            >
              {busy ? 'Merging…' : 'Confirm merge'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirming(false)}
              className={buttonVariants({ variant: 'secondary', size: 'md' })}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const inputClass =
  'mt-1 w-full rounded-md border border-border px-3 py-2 text-sm text-tres-text-primary placeholder:text-tres-text-secondary focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action disabled:bg-muted';

function ModalShell({
  title,
  busy,
  onClose,
  onSubmit,
  children,
}: {
  title: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-card text-tres-text-primary shadow-xl">
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <h2 className="text-lg font-semibold text-tres-text-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'ml-4')}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy) onSubmit();
          }}
          className="px-5 py-4"
        >
          {children}
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className={buttonVariants({ variant: 'secondary', size: 'lg' })}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className={buttonVariants({ variant: 'primary', size: 'lg' })}
            >
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface EditFormProps {
  title: string;
  busy: boolean;
  error: string | null;
  confirmingDelete: boolean;
  deleteLabel: string;
  uri: string;
  onCancel: () => void;
  onSave: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  children: React.ReactNode;
}

function EditForm({
  title,
  busy,
  error,
  confirmingDelete,
  deleteLabel,
  uri,
  onCancel,
  onSave,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
  children,
}: EditFormProps) {
  return (
    <div className="space-y-3 rounded border border-tres-action/40 bg-tres-submenu-bg/40 p-3">
      <p className="text-xs uppercase tracking-wide text-tres-text-secondary">{title}</p>
      {children}
      <p className="flex items-start gap-1 break-all font-mono text-[10px] text-tres-text-secondary">
        <span className="min-w-0">{uri}</span>
        <CopyUriButton uri={uri} />
      </p>
      {error && <ErrorBox message={error} />}
      {confirmingDelete ? (
        <div className={cn(messageClass('warning', 'normal'), 'flex flex-wrap items-center gap-2')}>
          <span className="text-tres-text-primary">
            Delete <span className="font-medium">{deleteLabel}</span>?
          </span>
          <span className="text-xs text-tres-text-secondary">
            Blocked if it still has dependents.
          </span>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={onCancelDelete}
              disabled={busy}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirmDelete}
              disabled={busy}
              className={buttonVariants({ variant: 'dangerFilled', size: 'sm' })}
            >
              {busy ? 'Deleting…' : 'Yes, delete'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between gap-2">
          <button
            type="button"
            onClick={onAskDelete}
            disabled={busy}
            className={buttonVariants({ variant: 'danger', size: 'sm' })}
          >
            Delete…
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className={buttonVariants({ variant: 'secondary', size: 'md' })}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={busy}
              className={buttonVariants({ variant: 'primary', size: 'md' })}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LabelField({
  value,
  onChange,
  disabled,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  autoFocus?: boolean;
}) {
  return (
    <label className="mb-3 block">
      <span className="text-sm font-medium text-tres-text-primary">
        Label<span className="ml-1 text-tres-error">*</span>
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoFocus={autoFocus}
        maxLength={200}
        className={inputClass}
      />
    </label>
  );
}

function CommentField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <label className="mb-3 block">
      <span className="text-sm font-medium text-tres-text-primary">Comment (:comment)</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={3}
        maxLength={1000}
        className={inputClass}
        placeholder="A first comment — add definitions and scope notes after creating."
      />
    </label>
  );
}

function KindField({
  value,
  onChange,
  disabled,
}: {
  value: PropertyKind;
  onChange: (k: PropertyKind) => void;
  disabled: boolean;
}) {
  return (
    <label className="mb-3 block">
      <span className="text-sm font-medium text-tres-text-primary">Type</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as PropertyKind)}
        disabled={disabled}
        className={inputClass}
      >
        <option value="object">Object property (links to a class)</option>
        <option value="datatype">Datatype property (holds a literal value)</option>
      </select>
    </label>
  );
}

/** Dropdown text for a term: label plus the URI, so an author choosing a
 *  parent, domain, or range sees the token they will have to type elsewhere. */
function optionText(term: { uri: string; label: string | null }): string {
  return `${labelOf(term)} — ${term.uri}`;
}

/** Multi-select over the declared classes: selected items as removable
 *  chips + an add-dropdown of the not-yet-selected ones. */
function ClassMultiSelect({
  title,
  selected,
  options,
  labelMap,
  disabled,
  onChange,
}: {
  title: string;
  selected: string[];
  options: SchemaClass[];
  labelMap: Map<string, string>;
  disabled: boolean;
  onChange: (next: string[]) => void;
}) {
  const available = options.filter((o) => !selected.includes(o.uri));
  return (
    <div className="mb-3">
      <span className="text-sm font-medium text-tres-text-primary">{title}</span>
      <Chips
        selected={selected}
        labelMap={labelMap}
        disabled={disabled}
        onRemove={(uri) => onChange(selected.filter((s) => s !== uri))}
      />
      <select
        value=""
        disabled={disabled || available.length === 0}
        onChange={(e) => {
          if (e.target.value) onChange([...selected, e.target.value]);
        }}
        className={inputClass}
      >
        <option value="">
          {available.length === 0 ? '(no more classes)' : `+ add ${title.toLowerCase()}…`}
        </option>
        {available.map((o) => (
          <option key={o.uri} value={o.uri}>
            {optionText(o)}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Range editor: classes for an object property, XSD datatypes for a
 *  datatype property. */
function RangeEditor({
  kind,
  selected,
  classOptions,
  labelMap,
  disabled,
  onChange,
}: {
  kind: PropertyKind;
  selected: string[];
  classOptions: SchemaClass[];
  labelMap: Map<string, string>;
  disabled: boolean;
  onChange: (next: string[]) => void;
}) {
  if (kind === 'object') {
    return (
      <ClassMultiSelect
        title="Range"
        selected={selected}
        options={classOptions}
        labelMap={labelMap}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }
  const available = XSD_DATATYPES.filter((d) => !selected.includes(d.uri));
  return (
    <div className="mb-3">
      <span className="text-sm font-medium text-tres-text-primary">Range</span>
      <Chips
        selected={selected}
        labelMap={labelMap}
        disabled={disabled}
        onRemove={(uri) => onChange(selected.filter((s) => s !== uri))}
      />
      <select
        value=""
        disabled={disabled || available.length === 0}
        onChange={(e) => {
          if (e.target.value) onChange([...selected, e.target.value]);
        }}
        className={inputClass}
      >
        <option value="">{available.length === 0 ? '(no more datatypes)' : '+ add datatype…'}</option>
        {available.map((d) => (
          <option key={d.uri} value={d.uri}>
            {d.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Chips({
  selected,
  labelMap,
  disabled,
  onRemove,
}: {
  selected: string[];
  labelMap: Map<string, string>;
  disabled: boolean;
  onRemove: (uri: string) => void;
}) {
  if (selected.length === 0) return null;
  return (
    <ul className="mt-1 flex flex-wrap gap-1.5">
      {selected.map((uri) => (
        <li
          key={uri}
          className="inline-flex items-center gap-1 rounded border border-tres-action/30 bg-tres-submenu-bg px-2 py-0.5 text-sm text-tres-text-primary"
          title={uri}
        >
          {labelMap.get(uri) ?? localName(uri)}
          <span className="select-text break-all font-mono text-[10px] text-tres-text-secondary">
            {uri}
          </span>
          <button
            type="button"
            onClick={() => onRemove(uri)}
            disabled={disabled}
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
            aria-label={`Remove ${labelMap.get(uri) ?? localName(uri)}`}
          >
            ✕
          </button>
        </li>
      ))}
    </ul>
  );
}

function DetailHeader({
  title,
  uri,
  canEdit,
  onEdit,
  badge,
  swatchColor,
}: {
  title: string;
  uri: string;
  canEdit: boolean;
  onEdit: () => void;
  badge: string;
  swatchColor?: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {swatchColor && (
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: swatchColor }}
              aria-hidden
            />
          )}
          <h2 className="text-lg font-semibold text-tres-text-primary">{title}</h2>
          <span
            className="rounded bg-muted px-1.5 py-0 font-mono text-[10px] text-tres-text-secondary"
            title="Type"
          >
            {badge}
          </span>
        </div>
        <p className="mt-0.5 flex items-start gap-1 break-all font-mono text-xs text-tres-text-secondary">
          <span className="min-w-0">{uri}</span>
          <CopyUriButton uri={uri} />
        </p>
      </div>
      {canEdit && (
        <button
          type="button"
          onClick={onEdit}
          className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'shrink-0')}
          title="Edit or delete"
        >
          Edit
        </button>
      )}
    </div>
  );
}

// Every documentation field a class or property carries, in reading order:
// the definition, the description (:comment), the scope note — how this
// store reads the term, including what it is NOT for — the alternative
// labels, notes and examples. The panel used to show the first :comment
// alone, so a term with a definition and a scope note read "No description."
const DOCUMENTATION_ORDER = [
  ':definition',
  ':comment',
  ':scopeNote',
  ':altLabel',
  ':note',
  ':example',
] as const;
const DOCUMENTATION_TITLES: Record<(typeof DOCUMENTATION_ORDER)[number], string> = {
  ':definition': 'Definition',
  ':comment': 'Description',
  ':scopeNote': 'Scope note — how to read this term',
  ':altLabel': 'Also called',
  ':note': 'Note',
  ':example': 'Example',
};

export function DocumentationSections({ annotations }: { annotations: AnnotationValue[] }) {
  const groups = DOCUMENTATION_ORDER.map(
    (predicate) => [predicate, annotations.filter((a) => a.predicate === predicate)] as const,
  ).filter(([, values]) => values.length > 0);
  if (groups.length === 0) {
    return (
      <DetailSection title="Description">
        <p className="text-sm italic text-tres-text-secondary">No description.</p>
      </DetailSection>
    );
  }
  return (
    <>
      {groups.map(([predicate, values]) => (
        <DetailSection key={predicate} title={DOCUMENTATION_TITLES[predicate]}>
          {predicate === ':altLabel' ? (
            <p className="text-sm text-tres-text-primary">
              {values.map((v) => v.value).join(' · ')}
            </p>
          ) : (
            values.map((v, i) => (
              <p key={i} className="text-sm text-tres-text-primary">
                {v.value}
              </p>
            ))
          )}
        </DetailSection>
      ))}
    </>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-tres-text-secondary">
        {title}
      </h3>
      {children}
    </div>
  );
}

/** A list of URI references. In-schema URIs are clickable (navigate to the
 *  term); external ones (owl:Thing, xsd:*) render as plain labels. */
function UriList({
  uris,
  labelMap,
  inSchema,
  onSelect,
}: {
  uris: string[];
  labelMap: Map<string, string>;
  inSchema: Set<string>;
  onSelect: (uri: string) => void;
}) {
  return (
    <ul className="space-y-0.5 text-sm">
      {uris.map((uri) => {
        const text = labelMap.get(uri) ?? localName(uri);
        return (
          <li key={uri} className="flex items-center gap-1">
            {inSchema.has(uri) ? (
              <button
                type="button"
                onClick={() => onSelect(uri)}
                className={cn(buttonVariants({ variant: 'link', size: 'md' }), 'text-left')}
                title={uri}
              >
                {text}
              </button>
            ) : (
              <span className="text-tres-text-primary" title={uri}>
                {text}
              </span>
            )}
            <span className="select-text break-all font-mono text-[10px] text-tres-text-secondary">
              {uri}
            </span>
            <CopyUriButton uri={uri} />
          </li>
        );
      })}
    </ul>
  );
}

/** The term's URI, always visible under its label in the tree. This is the
 *  token an author has to type into a rule or a query, so it is shown in full
 *  (wrapped, never truncated) and stays selectable — a label-only tree is what
 *  let a rule be written against a URI that did not exist. */
function TermUri({ uri, needle }: { uri: string; needle: string }) {
  return (
    <span className="mt-0.5 block select-text break-all font-mono text-[10px] leading-tight text-tres-text-secondary">
      <Highlighted text={uri} needle={needle} />
    </span>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className={messageClass('warning', 'normal')}>
      {message}
    </div>
  );
}

function Highlighted({ text, needle }: { text: string; needle: string }) {
  if (!needle) return <>{text}</>;
  const lower = text.toLowerCase();
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const idx = lower.indexOf(needle, cursor);
    if (idx === -1) {
      parts.push(text.slice(cursor));
      break;
    }
    if (idx > cursor) parts.push(text.slice(cursor, idx));
    parts.push(
      <mark key={`${idx}-${needle}`} className="bg-tres-warning/30 px-0 text-tres-text-primary">
        {text.slice(idx, idx + needle.length)}
      </mark>,
    );
    cursor = idx + needle.length;
  }
  return <>{parts}</>;
}

function Caret({ open }: { open: boolean }) {
  return (
    <span
      className={cn(
        'inline-block h-3 w-3 transform text-tres-text-secondary transition-transform',
        open ? 'rotate-90' : 'rotate-0',
      )}
      aria-hidden
    >
      ▶
    </span>
  );
}

// ---- pure helpers ----

function buildLabelMap(schema: OntologySchema | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!schema) return map;
  for (const c of schema.classes) map.set(c.uri, labelOf(c));
  for (const p of schema.properties) map.set(p.uri, labelOf(p));
  for (const d of XSD_DATATYPES) map.set(d.uri, `xsd:${d.label}`);
  return map;
}

function matchesTerm(term: { uri: string; label: string | null }, needle: string): boolean {
  return labelOf(term).toLowerCase().includes(needle) || term.uri.toLowerCase().includes(needle);
}

function filterForest(forest: ClassTreeNode[], needle: string): ClassTreeNode[] {
  if (!needle) return forest;
  const out: ClassTreeNode[] = [];
  for (const node of forest) {
    const kids = filterForest(node.children, needle);
    if (matchesTerm(node.cls, needle) || kids.length > 0) {
      out.push({ cls: node.cls, children: kids });
    }
  }
  return out;
}

/** Every class above `uri` in the schema shown, so a linked term's branch opens. */
function ancestorsInSchema(uri: string, classes: { uri: string; super_classes: string[] }[]): string[] {
  const byUri = new Map(classes.map((c) => [c.uri, c]));
  const found = new Set<string>();
  const stack = [uri];
  while (stack.length > 0) {
    for (const parent of byUri.get(stack.pop()!)?.super_classes ?? []) {
      if (found.has(parent)) continue;
      found.add(parent);
      stack.push(parent);
    }
  }
  return [...found];
}

function collectForestUris(forest: ClassTreeNode[]): string[] {
  const out: string[] = [];
  const walk = (nodes: ClassTreeNode[]) => {
    for (const n of nodes) {
      out.push(n.cls.uri);
      walk(n.children);
    }
  };
  walk(forest);
  return out;
}

/** Order-insensitive set equality for two string arrays. */
/** Trim values and drop blank rows before comparing/sending. */
function normalizedAnnotations(list: AnnotationValue[]): AnnotationValue[] {
  return list.map((a) => ({ ...a, value: a.value.trim() })).filter((a) => a.value !== '');
}

/** Order-insensitive equality on (predicate, value, lang) triples. */
function sameAnnotations(a: AnnotationValue[], b: AnnotationValue[]): boolean {
  if (a.length !== b.length) return false;
  const key = (x: AnnotationValue) => `${x.predicate}\u0001${x.value}\u0001${x.lang ?? ''}`;
  const as = a.map(key).sort();
  const bs = b.map(key).sort();
  return as.every((v, i) => v === bs[i]);
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  return b.every((x) => sa.has(x));
}
